import React from 'react';
import queryString from 'query-string';
import { JobsiteTabProps } from 'containers/jobsite/types';
import { Filter } from '@odin-labs/components';
import { usePageQueryParams } from 'utils/usePageQueryParams';
import moment from 'moment/moment';
import { getCurrentISOFormattedDate, momentISODateFormatter } from 'utils';
import {
  getFilterItems,
  JobsiteOverviewFilters,
  peakTimeSeriesValue,
} from 'containers/jobsite/tabs/jobsiteOverview/helpers';
import {
  TimeSeriesInputInterval,
  useGetJobsiteOverviewDataQuery,
} from 'apollo/generated/client-operations';
import { JobsiteOverviewTimeSeriesChart } from 'containers/jobsite/tabs/jobsiteOverview/components/JobsiteOverviewTimeSeriesChart';
import { JobsiteOverviewLogsSection } from 'containers/jobsite/tabs/jobsiteOverview/components/JobsiteOverviewLogsSection';
import { JobsiteOverviewOnboardingCard } from 'containers/jobsite/tabs/jobsiteOverview/components/JobsiteOverviewOnboardingCard';
import { JobsiteOverviewSection } from 'containers/jobsite/tabs/jobsiteOverview/components/JobsiteOverviewSection';
import { JobsiteOverviewWeatherCard } from 'containers/jobsite/tabs/jobsiteOverview/components/JobsiteOverviewWeatherCard';
import { JobsiteOverviewInfo } from 'containers/jobsite/tabs/jobsiteOverview/components/jobsiteOverviewInfo';
import { odinChartColors } from 'containers/reportDashboard/types';

const FORM_KEYS = [
  'vehicle-registration',
  'punch-list-log',
  'notias-exterior-daily-log',
  'daily-log-notias',
  'work-units-log',
  'safety-observation',
  'daily-log',
  'visitor-log',
  'site-safety',
  'inspection-log',
  'pre-task-safety-analysis',
  'daily-injury-survey',
  'fire-extinguisher-log',
];

export function JobsiteOverviewTabContainer({
  jobsiteId,
  baseRoute,
  onTabApiChange,
}: JobsiteTabProps): React.ReactElement {
  const { today, todayString } = React.useMemo(() => {
    const t = getCurrentISOFormattedDate();
    return { today: moment(t), todayString: t };
  }, []);

  const {
    date,
    updateUrl,
    loading: isUrlLoading,
  } = usePageQueryParams({
    defaultValues: {
      date: today,
    },
  });

  const {
    data,
    loading: isDataLoading,
    refetch: refetchOverviewData,
  } = useGetJobsiteOverviewDataQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !jobsiteId,
    variables: {
      jobsiteId,
      timeSeriesInput: {
        startDateTime: moment.utc(date).startOf('day').toDate(),
        endDateTime: moment.utc(date).endOf('day').toDate(),
        interval: TimeSeriesInputInterval.Every_1Hour,
      },
      weatherInput: {
        createIfNotExists: true,
        dates: [date.format(momentISODateFormatter)],
      },
      jobsiteFormSubmissionsInput: {
        startDate: moment.utc(date).startOf('day').toDate(),
        endDate: moment.utc(date).endOf('day').toDate(),
        formKeys: FORM_KEYS,
      },
    },
  });

  const filterItems = React.useMemo(
    () =>
      getFilterItems({
        date,
        today,
      }),
    [date, today],
  );

  const onFilterChangeHandler = React.useCallback(
    (changedFilters: Partial<JobsiteOverviewFilters>): void => {
      updateUrl({
        ...changedFilters,
      });
    },
    [updateUrl],
  );

  // The Info block owns its own query, so capture its refetch and compose it
  // with the overview data refetch, exposing a single reload for the header.
  const refetchInfoRef = React.useRef<(() => void) | null>(null);
  const onInfoRefetchReady = React.useCallback((refetch: () => void) => {
    refetchInfoRef.current = refetch;
  }, []);

  React.useEffect(() => {
    onTabApiChange({
      refetchData: () => {
        refetchOverviewData();
        refetchInfoRef.current?.();
      },
    });
  }, [onTabApiChange, refetchOverviewData]);

  const onboardingStats = React.useMemo(() => {
    return {
      Started:
        data?.getJobsite?.jobsiteStats?.numberOfOrientationWorkersInProgress,
      Completed:
        data?.getJobsite?.jobsiteStats?.numberOfOrientationWorkersCompleted,
      'Needs attn.':
        (data?.getJobsite?.jobsiteStats
          ?.numberOfOrientationWorkersNeedingBadging ?? 0) +
        (data?.getJobsite?.jobsiteStats
          ?.numberOfOrientationWorkersNeedingMedical ?? 0),
    };
  }, [data?.getJobsite?.jobsiteStats]);

  const reportsQuery = React.useMemo(() => {
    const dateString = moment(date).format(momentISODateFormatter);
    return queryString.stringify({
      jobsiteIds: jobsiteId,
      startDate: dateString,
      endDate: dateString,
    });
  }, [date, jobsiteId]);

  const workerSessions = data?.getJobsite?.timeSeries?.nowBoundedSessions ?? [];
  const contractorSessions =
    data?.getJobsite?.timeSeries?.nowBoundedContractorSessions ?? [];
  // TODO - Uncomment or remove with Site Access section once design/purpose is revisited
  // const inboundAccessEvents =
  //   data?.getJobsite?.timeSeries?.inboundAccessEvents ?? [];
  // const outboundAccessEvents =
  //   data?.getJobsite?.timeSeries?.outboundAccessEvents ?? [];
  const formSubmissions = data?.getJobsite?.jobsiteFormSubmissions?.edges ?? [];
  const hasEnabledLogTypes = React.useMemo(
    () =>
      (data?.getJobsite?.jobsiteForms?.edges ?? []).some(({ node }) =>
        FORM_KEYS.includes(node.form.key),
      ),
    [data?.getJobsite?.jobsiteForms?.edges],
  );
  const weatherConditions =
    data?.getJobsite?.weather?.edges?.[0]?.node?.weatherConditions ?? [];
  const jobsiteTimeZone = data?.getJobsite?.timeZone ?? null;

  return (
    <div className="odin-flex odin-flex-col odin-gap-6">
      <JobsiteOverviewInfo
        jobsiteId={jobsiteId}
        baseRoute={baseRoute}
        onRefetchReady={onInfoRefetchReady}
      />
      <div className="odin-flex odin-flex-col odin-gap-4">
        <h2 className="odin-text-lg odin-font-semibold odin-text-odin-navy">
          Dashboard
        </h2>
        <Filter
          items={filterItems}
          loading={isUrlLoading}
          onChange={onFilterChangeHandler}
        />
      </div>
      <div className="odin-grid odin-grid-cols-1 lg:odin-grid-cols-2 odin-gap-6">
        <JobsiteOverviewTimeSeriesChart
          title="Workers"
          linkTo={`/reports/workers?${reportsQuery}`}
          data={workerSessions}
          color={odinChartColors[3]}
          loading={isDataLoading}
          emptyMessage="No worker activity on this date"
          stats={[
            {
              label: 'Total',
              value: data?.getJobsite?.jobsiteStats?.numberOfActiveWorkers,
            },
            {
              label: 'On site',
              value:
                date && date.format(momentISODateFormatter) === todayString
                  ? data?.getJobsite?.jobsiteStats?.numberOfOnSiteWorkers
                  : null,
            },
            { label: "Day's peak", value: peakTimeSeriesValue },
          ]}
        />
        <JobsiteOverviewTimeSeriesChart
          title="Contractors"
          linkTo={`/reports?${reportsQuery}`}
          data={contractorSessions}
          color={odinChartColors[6]}
          loading={isDataLoading}
          emptyMessage="No contractor activity on this date"
          stats={[
            {
              label: 'Total',
              value: data?.getJobsite?.jobsiteStats?.numberOfActiveContractors,
            },
            {
              label: 'On site',
              value:
                date && date.format(momentISODateFormatter) === todayString
                  ? data?.getJobsite?.jobsiteStats?.numberOfOnSiteContractors
                  : null,
            },
            { label: "Day's peak", value: peakTimeSeriesValue },
          ]}
        />
      </div>
      <JobsiteOverviewSection label="Site status">
        <div className="odin-grid odin-grid-cols-1 lg:odin-grid-cols-2 odin-gap-6">
          <JobsiteOverviewOnboardingCard
            onboardingStats={onboardingStats}
            linkQueryString={reportsQuery}
            loading={isDataLoading}
          />
          <JobsiteOverviewWeatherCard
            weatherConditions={weatherConditions}
            timeZone={jobsiteTimeZone}
            date={date}
            loading={isDataLoading}
          />
        </div>
      </JobsiteOverviewSection>
      {/* Hide the section entirely for jobsites with no log types enabled. While
          data is loading we don't yet know, so keep it mounted to show the skeleton. */}
      {(isDataLoading || hasEnabledLogTypes) && (
        <JobsiteOverviewLogsSection
          submissions={formSubmissions}
          linkQueryString={reportsQuery}
          loading={isDataLoading}
        />
      )}
      {/* TODO - Uncomment or remove Site Access section once design/purpose is revisited  */}
      {/* <JobsiteOverviewSection label="Site access"> */}
      {/*  <div className="odin-grid odin-grid-cols-1 lg:odin-grid-cols-2 odin-gap-6"> */}
      {/*    <JobsiteOverviewTimeSeriesChart */}
      {/*      title="Inbound" */}
      {/*      data={inboundAccessEvents} */}
      {/*      color={odinChartColors[5]} */}
      {/*      stats={[ */}
      {/*        { label: 'Total', value: sumTimeSeriesValues }, */}
      {/*        { label: "Day's peak", value: peakTimeSeriesValue }, */}
      {/*      ]} */}
      {/*    /> */}
      {/*    <JobsiteOverviewTimeSeriesChart */}
      {/*      title="Outbound" */}
      {/*      data={outboundAccessEvents} */}
      {/*      color={odinChartColors[7]} */}
      {/*      stats={[ */}
      {/*        { label: 'Total', value: sumTimeSeriesValues }, */}
      {/*        { label: "Day's peak", value: peakTimeSeriesValue }, */}
      {/*      ]} */}
      {/*    /> */}
      {/*  </div> */}
      {/* </JobsiteOverviewSection> */}
    </div>
  );
}
